import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { Home, MessageSquare, Bot, Inbox, Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTenant } from "@/context/tenant-context";
import { goalsApi, type Goal, type GoalMessage } from "@/api/goals";
import { queryKeys } from "@/lib/query-keys";

interface MobileBottomTabsProps {
  selectedAgentId: string | null;
  onOpenAgentSheet: () => void;
  onOpenDrawer: () => void;
}

export function MobileBottomTabs({
  selectedAgentId,
  onOpenAgentSheet,
  onOpenDrawer,
}: MobileBottomTabsProps) {
  const { tenantId } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const base = `/tenants/${tenantId}`;

  const isHome = location.pathname === `${base}/dashboard`;
  const isChat =
    location.pathname === `${base}/workspace` ||
    location.pathname === `${base}/chat`;
  const isAgents =
    location.pathname === `${base}/agents` ||
    location.pathname.startsWith(`${base}/agents/`);
  const isInbox = location.pathname === `${base}/inbox`;

  const { data: goals = [] } = useQuery({
    queryKey: queryKeys.goals.byCompany(tenantId),
    queryFn: () => goalsApi.list(tenantId),
    staleTime: 30_000,
  });
  const ownedGoalIds = goals
    .filter((g: Goal) => g.ownerId)
    .map((g: Goal) => g.id);
  const { data: msgs = [] } = useQuery({
    queryKey: ["goal-messages-inbox", tenantId],
    queryFn: async () => {
      const acc: GoalMessage[] = [];
      for (const gid of ownedGoalIds) {
        acc.push(...(await goalsApi.listMessages(gid)));
      }
      return acc;
    },
    enabled: ownedGoalIds.length > 0,
    staleTime: 30_000,
  });
  const unread = msgs.filter(
    (m) => m.direction === "agent_to_human" && !m.readAt,
  ).length;

  const goToChat = () => {
    const params = selectedAgentId ? `?agentId=${selectedAgentId}` : "";
    navigate(`${base}/workspace${params}`);
  };

  return (
    <nav
      className="sm:hidden fixed inset-x-0 z-40 px-3.5 pointer-events-none"
      style={{ bottom: "calc(14px + env(safe-area-inset-bottom, 0px))" }}
    >
      <div className="glass-raised pointer-events-auto grid h-[62px] grid-cols-5 items-stretch rounded-[26px] px-1.5">
        <TabButton icon={Home} label="Home" active={isHome} onClick={() => navigate(`${base}/dashboard`)} />
        <TabButton icon={MessageSquare} label="Chat" active={isChat} onClick={goToChat} />
        <TabButton icon={Bot} label="Agents" active={isAgents} onClick={() => navigate(`${base}/agents`)} />
        <TabButton icon={Inbox} label="Inbox" active={isInbox} badge={unread} onClick={() => navigate(`${base}/inbox`)} />
        <TabButton icon={Menu} label="More" active={false} onClick={onOpenDrawer} />
      </div>
    </nav>
  );
}

function TabButton({
  icon: Icon,
  label,
  active,
  badge,
  onClick,
}: {
  icon: typeof MessageSquare;
  label: string;
  active: boolean;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "relative flex flex-col items-center justify-center gap-1 transition-colors",
        active ? "text-accent" : "text-text-muted hover:text-text-primary",
      )}
    >
      <div className="relative flex items-center justify-center">
        {/* accentSoft rounded halo behind the active icon */}
        {active && (
          <span
            aria-hidden="true"
            className="absolute"
            style={{
              inset: "-5px -14px",
              borderRadius: 16,
              background: "var(--mg-accent-soft, rgba(59,119,216,0.12))",
            }}
          />
        )}
        <Icon size={21} strokeWidth={active ? 2.2 : 1.7} className="relative" />
        {badge !== undefined && badge > 0 && (
          <span
            className="absolute z-[1] flex items-center justify-center rounded-full px-1 font-bold text-white"
            style={{
              minWidth: 15,
              height: 15,
              top: -5,
              right: -9,
              fontSize: 9.5,
              background: "#E5484D",
            }}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
      <span className="relative font-medium tracking-tight" style={{ fontSize: 9.5 }}>
        {label}
      </span>
    </button>
  );
}
